import { cx } from "@emotion/css";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Popper from "@mui/material/Popper";
import { ArrowLeft, ChevronRight } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import isMobile from "src/helpers/isMobile";
import type { ActionItem } from "./types";
import { useSubmenuHover } from "./useSubmenuHover";

export type { ActionItem };

interface ActionDivider {
	isDivider: true;
}

type ActionEntry = ActionItem | ActionDivider;

interface DefaultActionsProps {
	defaultItems: (ActionEntry | null | false)[];
}

const isDivider = (entry: ActionEntry): entry is ActionDivider =>
	"isDivider" in entry;

export default function DefaultActions({ defaultItems }: DefaultActionsProps) {
	const { t } = useTranslation();
	const [mobileSubmenu, setMobileSubmenu] = useState<ActionItem | null>(null);
	const { activeSubmenu, close: closeSubmenu, getHandlers } = useSubmenuHover();

	const entries = [...defaultItems].filter(Boolean) as ActionEntry[];
	const hasItems = entries.some((entry) => !isDivider(entry));

	if (!hasItems) {
		return <div className="p-8">{t("menuActions.emptyActions")}</div>;
	}

	if (isMobile() && mobileSubmenu) {
		return (
			<>
				<MenuItem
					disableRipple
					className="menu-item"
					data-no-close
					onClick={(e) => {
						e.stopPropagation();
						setMobileSubmenu(null);
					}}
				>
					<ListItemIcon>
						<ArrowLeft className="icon-small" />
					</ListItemIcon>
					<ListItemText
						primary={
							<span className="size-14-light">{mobileSubmenu.title}</span>
						}
					/>
				</MenuItem>
				{(mobileSubmenu.items ?? []).map((item, index) => (
					<MenuItem
						key={item.testId ?? `${item.title}-${index}`}
						onClick={(e) => {
							e.stopPropagation();
							item.onClick?.();
						}}
						disableRipple
						className={cx("menu-item", item.className)}
						data-test-id={item.testId}
					>
						{item.Icon && <ListItemIcon>{item.Icon}</ListItemIcon>}
						<ListItemText
							primary={<span className="size-14-light">{item.title}</span>}
						/>
					</MenuItem>
				))}
			</>
		);
	}

	return (
		<>
			{entries.map((entry, index) => {
				if (isDivider(entry)) {
					return <div key={`divider-${index}`} className="menu-divider" />;
				}

				const item = entry;
				const isNested = !!item.items?.length;
				const hoverHandlers = isNested && !isMobile() ? getHandlers(item) : {};

				return (
					<MenuItem
						key={item.testId ?? `${item.title}-${index}`}
						onClick={(e) => {
							e.stopPropagation();
							if (isNested) {
								if (isMobile()) {
									setMobileSubmenu(item);
								} else {
									closeSubmenu();
								}
							} else {
								item.onClick?.();
							}
						}}
						disableRipple
						className={cx("menu-item", item.className)}
						data-test-id={item.testId}
						{...(isNested ? { "data-no-close": true } : {})}
						{...hoverHandlers}
					>
						{item.Icon && <ListItemIcon>{item.Icon}</ListItemIcon>}
						<ListItemText
							primary={<span className="size-14-light">{item.title}</span>}
						/>
						{isNested && <ChevronRight className="icon-small" />}
					</MenuItem>
				);
			})}
			{activeSubmenu && (
				<ClickAwayListener onClickAway={closeSubmenu}>
					<Popper
						open
						anchorEl={activeSubmenu.anchor}
						placement="right-start"
						className="popper"
					>
						{(activeSubmenu.item.items ?? []).map((item, index) => (
							<MenuItem
								key={item.testId ?? `${item.title}-${index}`}
								onClick={(e) => {
									e.stopPropagation();
									item.onClick?.();
									closeSubmenu();
								}}
								disableRipple
								className={cx("menu-item", item.className)}
								data-test-id={item.testId}
							>
								{item.Icon && <ListItemIcon>{item.Icon}</ListItemIcon>}
								<ListItemText
									primary={<span className="size-14-light">{item.title}</span>}
								/>
							</MenuItem>
						))}
					</Popper>
				</ClickAwayListener>
			)}
		</>
	);
}
